import { AppDispatch } from "../store";
import {
  cancelDrag,
  dropTask,
  setEditMode,
  setSessionStatus,
  setRulesBroken,
  setCustomGanttChartData,
  TaskBeingDraggedObject,
  setActiveSessionStatus,
} from "../store/dragAndDrop";
import {
  clearGanttChartData,
  setGanttChartData,
  setSelectedGanttChartTab,
} from "../store/ganttChart";
import { setNotification } from "../store/toastNotifications";
import { IntersectionDetails } from "../types/dragAndDrop";
import {
  isShiftLocation,
  MoveWorkTaskDragAndDropCommand,
} from "./DragAndDropApi";
import {
  GanttChartData,
  GanttTabBarTab,
  Plan,
  WorkTask,
} from "../types/ganttChart";
import ganttCHartValues, {
  ganttChartVerticalValues,
} from "../utils/ganttChartValues";
import { TimeUtil } from "../utils/TimeUtil";
import { CommandResponse, DragAndDropApi } from "./DragAndDropApi";
import { IntersectionResult } from "./IntervalTree";

class DragAndDropService {
  static findShiftData = (
    unplannedColumnWidth: number,
    columnWidth: number,
    xPosition: number
  ): number => {
    const index =
      (xPosition -
        unplannedColumnWidth -
        ganttChartVerticalValues.timelineColumn) /
      columnWidth;
    // round down (always down) index and return
    return ~~index;
  };

  static findStartTime = (
    startTime: string,
    originalYPostion: number,
    newYPostion: number,
    pixelsPerMinute: number
  ) => {
    const differenceInY = newYPostion - originalYPostion;
    const differenceInMinutes = differenceInY / pixelsPerMinute;
    const differenceInMilliseconds =
      TimeUtil.fromMinutesToMilliseconds(differenceInMinutes);

    return differenceInMilliseconds;
  };

  static findPreviousTask(tasks: Plan, newStartTime: number) {
    // Combine worktasks and operational tasks and map to only what we need
    const combinedTaskArray = [
      ...tasks.workTasks,
      ...tasks.operationalTasks,
    ].map((item) => ({
      taskId: item.idTask,
      endTime: item.end,
    }));

    // Find previous task
    return combinedTaskArray.reduce((closest, current) => {
      const currentDiff = Math.abs(
        new Date(current.endTime).getTime() - newStartTime
      );

      const closestDiff = Math.abs(
        new Date(closest.endTime).getTime() - newStartTime
      );

      return currentDiff < closestDiff ? current : closest;
    }).taskId;
  }

  static async initiateSessionAndStoreData(
    initialState: GanttChartData,
    dispatch: AppDispatch
  ) {
    try {
      const response = await DragAndDropApi.initiateSession(initialState);
      dispatch(setCustomGanttChartData(response.state));
      dispatch(setEditMode(true));
      dispatch(setSelectedGanttChartTab("Custom"));

      // Get session status to retrieve rulesBroken
      try {
        const sessionStatus = await DragAndDropApi.getSessionStatus();
        dispatch(setActiveSessionStatus(sessionStatus));
      } catch (error) {
        console.error("Failed to get session status:", error);
      }
      return response;
    } catch (error: any) {
      const data: CommandResponse = error.response.data;
      dispatch(
        setNotification({
          message: data.message,
          type: "move_task_failed",
          variant: "error",
        })
      );
    }
  }

  static async executeDropCommand(
    commandData: {
      taskBeingDragged: TaskBeingDraggedObject;
      moveTaskCommandObject: IntersectionDetails | undefined;
      customGanttChartData: GanttChartData | undefined;
    },
    ganttData: GanttChartData | undefined,
    dispatch: AppDispatch,
    selectedGanttChartTab: GanttTabBarTab
  ) {
    try {
      const command = buildMoveCommand(
        commandData.taskBeingDragged,
        commandData.moveTaskCommandObject!
      );

      const initialState = !commandData.customGanttChartData
        ? ganttData
        : undefined;

      const response = await DragAndDropApi.executeCommand(
        command,
        initialState
      );

      // TODO move out
      dispatch(setEditMode(true));
      dispatch(dropTask());
      if (selectedGanttChartTab !== "Custom") {
        dispatch(setSelectedGanttChartTab("Custom"));
      }

      // Get session status to retrieve rulesBroken
      try {
        const sessionStatus = await DragAndDropApi.getSessionStatus();
        dispatch(setActiveSessionStatus(sessionStatus));
      } catch (error) {
        console.error("Failed to get session status:", error);
      }

      return { success: true, data: response };
    } catch (error: any) {
      const data: CommandResponse = error.response.data;
      dispatch(
        setNotification({
          message: data.message,
          type: "move_task_failed",
          variant: "error",
        })
      );
      dispatch(cancelDrag());
      return { success: false, error };
    }
  }

  static async getCustomSessionState(dispatch: AppDispatch) {
    try {
      const response = await DragAndDropApi.getSessionStatus();
      dispatch(setEditMode(true));

      // dispatch(setGanttChartData(data.))
      return { success: true, data: response };
    } catch (error: any) {
      const data: CommandResponse = error.response.data;
      dispatch(
        setNotification({
          message: data.message,
          type: "move_task_failed",
          variant: "error",
        })
      );
      return { success: false, error };
    }
  }

  static async saveAndFinalizePlan(dispatch: AppDispatch) {
    try {
      const response = await DragAndDropApi.saveAndFinalize();
      if (response.success) {
        dispatch(setEditMode(false));
        dispatch(setSessionStatus(false));
        dispatch(setSelectedGanttChartTab("Optimised"));
        // Reset state
        dispatch(clearGanttChartData());
      }
      dispatch(
        setNotification({
          message: response.message,
          type: "saved_changes",
          variant: response.success ? "success" : "error",
        })
      );
      return response;
    } catch (error: any) {
      const data: CommandResponse = error.response.data;
      /* TODO: update notification type and consider error message. */
      dispatch(
        setNotification({
          message: data.message,
          type: "move_task_failed",
          variant: "error",
        })
      );
    }
  }
  static async undo(dispatch: AppDispatch) {
    try {
      const result = await DragAndDropApi.undo();

      // Update the custom tab with the returned state
      if (result.state) {
        dispatch(setCustomGanttChartData(result.state));
      }

      // Get session status to update rulesBroken after undo
      try {
        const sessionStatus = await DragAndDropApi.getSessionStatus();
        dispatch(setActiveSessionStatus(sessionStatus));
      } catch (error) {
        console.error("Failed to get session status after undo:", error);
      }
    } catch (error: any) {
      const data: CommandResponse = error.response.data;
      /* TODO: update notification type and consider error message. */
      dispatch(
        setNotification({
          message: data.message,
          type: "move_task_failed",
          variant: "error",
        })
      );
    }
  }
  static async shutDownSession(dispatch: AppDispatch) {
    /* TODO: Check if user is ready to shut down the edit tab */

    try {
      const response = await DragAndDropApi.endSession();
      if (response.success) {
        dispatch(setSelectedGanttChartTab("Optimised"));
        dispatch(setEditMode(false));
        dispatch(setSessionStatus(false));
      }
    } catch (error) {
      /* Handle error */
    }
  }

  static findIntersectionData(
    intersection: IntersectionResult,
    y: number
  ): {
    targetTaskId: number | null;
    destinationTaskIdBefore: number | null;
    destinationTaskIdAfter: number | null;
  } {
    // Use the targetTaskForIndicator calculated by the IntervalTree
    const targetTaskId = intersection.targetTaskForIndicator?.idTask || null;
    // Calculate destination task IDs for drop logic
    let destinationTaskIdBefore = null;
    let destinationTaskIdAfter = null;

    // Set destination tasks based on actual placement logic (not UI logic)
    if (intersection.intersectingTask) {
      // Original logic when dragging over a task
      if (intersection.isCloserToStart) {
        destinationTaskIdBefore = intersection.taskAbove?.idTask || null;
        destinationTaskIdAfter = intersection.intersectingTask?.idTask || null;
      } else {
        destinationTaskIdBefore = intersection.intersectingTask?.idTask || null;
        destinationTaskIdAfter = intersection.taskBelow?.idTask || null;
      }
    } else {
      // No intersecting task - placing between existing tasks
      destinationTaskIdBefore = intersection.taskAbove?.idTask || null;
      destinationTaskIdAfter = intersection.taskBelow?.idTask || null;
    }

    const result = {
      targetTaskId: targetTaskId,
      destinationTaskIdBefore: destinationTaskIdBefore,
      destinationTaskIdAfter: destinationTaskIdAfter,
    };

    return result;
  }
}

// Helper function for building move commands with new TaskLocation approach
const buildMoveCommand = (
  taskBeingDragged: TaskBeingDraggedObject,
  destination: IntersectionDetails
): MoveWorkTaskDragAndDropCommand => {
  return {
    workTaskId: taskBeingDragged.task.idTask,
    destinationWorkTaskIdBefore: destination.destinationWorkTaskIdBefore,
    destinationWorkTaskIdAfter: destination.destinationWorkTaskIdAfter,
    from: taskBeingDragged.sourceLocation,
    to: destination.destinationLocation,

    // Legacy fields for backward compatibility
    fromShiftId: isShiftLocation(taskBeingDragged.sourceLocation)
      ? taskBeingDragged.sourceLocation.shiftId
      : undefined,
    toShiftId: isShiftLocation(destination.destinationLocation)
      ? destination.destinationLocation.shiftId
      : undefined,
  };
};

export default DragAndDropService;
